import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { useCallback, useMemo } from "react"

import useUser from "@hooks/useUser"

import { UseScheduleHorizonProps } from "@organisms/ObjectsView/ObjectsView.types"

import { SCHEDULE_HORIZON_OPTIONS, ScheduleHorizon } from "@constants/scheduleHorizon"

dayjs.extend(timezone)
dayjs.extend(utc)

const DEFAULT_SCHEDULE_HORIZON: ScheduleHorizon = "all_dates"
const SCHEDULE_HORIZON_FILTER_PARAM = "scheduled_before"

// Ride the bound along with the rest of the list filters, so the queries stay unaware of the horizon.
function applyScheduleHorizon(filters: URLSearchParams | null, upperBound: string | null) {
    if (upperBound) {
        const filtersWithHorizon = new URLSearchParams(filters ?? undefined)
        filtersWithHorizon.set(SCHEDULE_HORIZON_FILTER_PARAM, upperBound)

        return filtersWithHorizon
    }

    return filters
}

// The window opens at the start of today in the company's timezone and covers the preset's fixed number of
// days, so it rolls forward daily. The bound is exclusive — midnight after the last day — which keeps the
// whole final day inside the window. `null` when no bound applies, or before the timezone is known.
function getScheduleHorizonUpperBound(horizon: ScheduleHorizon | undefined, preferredTimezone: string | undefined) {
    const selectedOption = SCHEDULE_HORIZON_OPTIONS.find((option) => option.value === horizon)

    if (!preferredTimezone || !selectedOption?.days) {
        return null
    }

    return dayjs().tz(preferredTimezone).startOf("day").add(selectedOption.days, "day").toISOString()
}

export default function useScheduleHorizon(props: UseScheduleHorizonProps) {
    const {
        objectType,
        activePhase,
        savedScheduleHorizon,
        saveScheduleHorizon,
        getSavedScheduleHorizonForTab,
        filters,
    } = props

    const { user } = useUser()

    // `useUser` seeds its atom in an effect, so the first render has no user; nothing is bound until it does.
    const preferredTimezone = user?.service_company?.preferred_timezone

    // The horizon only makes sense where jobs are still being dispatched.
    const isScheduleHorizonAvailable = objectType === "job" && activePhase === "open"

    const scheduleHorizon = savedScheduleHorizon ?? DEFAULT_SCHEDULE_HORIZON

    const scheduleHorizonUpperBound = useMemo(() => {
        if (isScheduleHorizonAvailable) {
            return getScheduleHorizonUpperBound(scheduleHorizon, preferredTimezone)
        }

        return null
    }, [isScheduleHorizonAvailable, scheduleHorizon, preferredTimezone])

    const listFilters = useMemo(
        () => applyScheduleHorizon(filters, scheduleHorizonUpperBound),
        [filters, scheduleHorizonUpperBound],
    )

    // Every tab keeps its own horizon, so a count badge is bound by the horizon of the tab it opens
    // rather than the active tab's; otherwise a badge counts one window and opens another.
    const getFiltersForTab = useCallback(
        (tab: string) => {
            if (isScheduleHorizonAvailable) {
                const tabUpperBound = getScheduleHorizonUpperBound(
                    getSavedScheduleHorizonForTab?.(tab),
                    preferredTimezone,
                )

                return applyScheduleHorizon(filters, tabUpperBound)
            }

            return filters
        },
        [isScheduleHorizonAvailable, getSavedScheduleHorizonForTab, preferredTimezone, filters],
    )

    const setScheduleHorizon = (horizon: ScheduleHorizon) => {
        saveScheduleHorizon?.(horizon)
    }

    return {
        scheduleHorizon,
        setScheduleHorizon,
        isScheduleHorizonAvailable,
        scheduleHorizonUpperBound,
        listFilters,
        getFiltersForTab,
    }
}
